import { useMemo } from "react";
import { map } from "lodash";
import { Sort } from "@charty/horizontal-bar-chart/model";
import { Facet } from "@linkfluence/models/facet";
import { Locale } from "@linkfluence/models/locale";
import { Metric } from "@linkfluence/models/metric";
import { getRangeSize } from "@tootools/date";
import { evolution, evolutionPercentagePoint, percent } from "@tootools/number";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { DatavizHorizontalBarChart } from "@radarly/models/dataviz";
import { FullFacet } from "@radarly/models/facets";
import { SearchFragmentTerm, getTermFromAggs } from "@radarly/models/search";
import { useRangeFromFragment } from "../../data";
import { computeData, getTermSeries } from "../data";

interface HookProps {
  loading: boolean;
  data: SearchFragmentTerm["data"];
  comparaisonData: SearchFragmentTerm["data"];
  fragment: SearchFragmentTerm;
  dataviz: DatavizHorizontalBarChart;
  facet: FullFacet;
}

function isTermReviewRating(facet: string) {
  return facet === Facet.ReviewRate;
}

function getPenetrationRateTotal(
  data: SearchFragmentTerm["data"],
  metric: Metric
) {
  return data && data.aggs.metrics ? data.aggs.metrics.metrics[metric] : null;
}

function getSort(
  sort: DatavizHorizontalBarChart["sort"],
  withAverageRating: boolean
): Sort {
  return withAverageRating ? "alpha" : sort === "mainUnit" ? "mainValue" : sort;
}

function getMeanSize(
  mainUnit: DatavizHorizontalBarChart["mainUnit"],
  range: ReturnType<typeof useRangeFromFragment>
) {
  return mainUnit === "average" ? getRangeSize(range) + 1 : 0;
}

function getSeriesWithEvolution(
  data: SearchFragmentTerm["data"],
  comparaisonData: SearchFragmentTerm["data"],
  facet: FullFacet,
  metric: Metric,
  locale: Locale,
  mean?: number,
  mainUnit?: DatavizHorizontalBarChart["mainUnit"]
) {
  const previousTotalCount = getTotalFromData(comparaisonData, metric);
  const currentTotalCount = getTotalFromData(data, metric);

  const values =
    data && data.aggs && facet && data.aggs[facet.id]
      ? data.aggs[facet.id].values
      : [];
  const computedData = computeData(values, facet, metric, mean);
  const computedEvolutions = computeData(
    comparaisonData &&
      comparaisonData.aggs &&
      facet &&
      comparaisonData.aggs[facet.id]
      ? comparaisonData.aggs[facet.id].values
      : [],
    facet,
    metric,
    mean
  );
  return map(getTermSeries(values, facet, locale), ({ id, label, color }) => {
    const count = computedData[id] || 0;
    const previousCount = computedEvolutions[id];
    if (!previousCount) {
      return {
        id,
        count,
        color,
        label,
        evolution: null,
      };
    }

    let evolutionValue = null;
    if (mainUnit === "percentage") {
      const before = percent(previousCount, previousTotalCount);
      const after = percent(count, currentTotalCount);
      evolutionValue = evolutionPercentagePoint(before, after);
    } else {
      evolutionValue = evolution(previousCount, count);
    }

    return {
      id,
      count,
      color,
      label,
      evolution: evolutionValue,
    };
  });
}

function getTotalFromData(data: SearchFragmentTerm["data"], metric: Metric) {
  return data && data.aggs && data.aggs.metrics
    ? data.aggs.metrics.metrics[metric]
    : null;
}

function useGauges({
  mainUnit,
  data,
  comparaisonData,
  facet,
  metric,
  range,
}: {
  mainUnit: DatavizHorizontalBarChart["mainUnit"];
  data: SearchFragmentTerm["data"];
  comparaisonData: SearchFragmentTerm["data"];
  facet: FullFacet;
  metric: Metric;
  range: ReturnType<typeof useRangeFromFragment>;
}) {
  const locale = useCurrentUserLocale();
  return useMemo(() => {
    const mean = getMeanSize(mainUnit, range);
    return getSeriesWithEvolution(
      data,
      comparaisonData,
      facet,
      metric,
      locale,
      mean,
      mainUnit
    );
  }, [mainUnit, range, data, comparaisonData, facet, metric, locale]);
}

export function useHorizontalBarChart({
  fragment,
  loading,
  facet,
  dataviz,
  data,
  comparaisonData,
}: HookProps) {
  const field = getTermFromAggs(fragment.request.aggs);
  const metric = fragment.request.aggs[field].metrics[0];
  const range = useRangeFromFragment(fragment, loading);
  const gauges = useGauges({
    data,
    comparaisonData,
    facet,
    metric,
    mainUnit: dataviz.mainUnit,
    range,
  });
  const withAverageRating = isTermReviewRating(facet.id);
  const sort = getSort(dataviz.sort, withAverageRating);
  const total = getPenetrationRateTotal(data, metric);
  return {
    gauges,
    sort,
    total,
    range,
    metric,
    withAverageRating,
    order: withAverageRating ? "asc" : null,
  };
}
